/**
 * ModuleUpdatesNotice.tsx — powiadomienie klienta o dostępnych aktualizacjach modułów
 * zainstalowanych z szablonu (Krok 5, kawałek C).
 *
 * Pyta GET /api/my/module-updates. Gdy ITE wypuściło nowszą wersję niż ta przypięta u
 * klienta, pokazuje baner nad treścią + modal z podglądem zmian (bezpieczne / wymagające
 * uwagi) — reuse etykiet zmian z „Historii wersji" panelu ITE.
 *
 * Render `null` gdy brak aktualizacji albo błąd (np. scope admina, gdzie /my/* zwraca 401) —
 * ten sam wzorzec co InstalledModulesMenu w Sidebarze (silent-on-error).
 *
 * „Pomiń" zapamiętujemy na koncie klienta (POST .../dismiss → kolumna na subskrypcji) — kolejna
 * wypuszczona wersja ma inny numer, więc powiadomienie wróci. „Zastosuj" = realna migracja u klienta.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowUpCircle } from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Banner } from '@/components/Banner';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { useToast } from '@/contexts/ToastContext';
import { useHostScope } from '@/hooks/useHostScope';
import { useReservedFieldSlugToast } from '@/hooks/useReservedFieldSlugToast';
import { api } from '@/lib/api';
import { INSTALLED_MODULES_QUERY_KEY } from '@/lib/hr/query-keys';

interface StructureChange {
  kind: string;
  entitySlug: string;
  entityName?: string;
  fieldSlug?: string;
  fieldName?: string;
  detail?: string;
  breaking: boolean;
}

interface ModuleUpdate {
  moduleId: string;
  moduleName: string;
  areaName: string | null;
  installedClientVersion: string;
  latestClientVersion: string;
  releaseNotes: string | null;
  changeCount: number;
  hasBreaking: boolean;
  changes: StructureChange[];
}

/** Odpowiedź POST .../apply — losy layoutów przy propagacji widoków (hub Krok 3). */
interface ApplyUpdateResponse {
  appliedClientVersion: string;
  layouts?: { entitySlug: string; layoutType: 'form' | 'list'; status: string }[];
}

/** Czytelny opis pojedynczej zmiany (encja › pole + ewentualny szczegół). */
function changeLabel(c: StructureChange, t: (k: string) => string): string {
  const target = c.fieldName
    ? `${c.entityName ?? c.entitySlug} › ${c.fieldName}`
    : (c.entityName ?? c.entitySlug);
  const kind = t(`moduleTemplateEditor.versions.change.${c.kind}`);
  return c.detail ? `${kind}: ${target} (${c.detail})` : `${kind}: ${target}`;
}

export function ModuleUpdatesNotice() {
  const { t } = useTranslation();
  const toast = useToast();
  const showReservedSlugToast = useReservedFieldSlugToast();
  const queryClient = useQueryClient();
  const scope = useHostScope();
  const [open, setOpen] = useState(false);

  // Feature wyłącznie tenant-scope. KRYTYCZNE: nie odpalać na admin/public — `/my/*` zwraca
  // tam 401, a globalny interceptor axios na 401 w scope admina robi redirect na /login
  // (admin nie ma refreshu). Bez tego gatingu baner wybijał admina z sesji na każdej stronie.
  const query = useQuery({
    queryKey: ['my', 'module-updates'],
    queryFn: async () => {
      const res = await api.get<{ updates: ModuleUpdate[] }>('/my/module-updates');
      return res.data.updates;
    },
    enabled: scope === 'tenant',
    staleTime: 60 * 1000,
    retry: false,
  });

  const applyMutation = useMutation({
    mutationFn: async (moduleId: string) =>
      (await api.post<ApplyUpdateResponse>(`/my/module-updates/${moduleId}/apply`)).data,
    onSuccess: (data) => {
      toast.show({ variant: 'success', description: t('tenant.moduleUpdates.applySuccess') });
      // Hub Krok 3: layouty zmienione po stronie klienta NIE są nadpisywane — notka,
      // żeby brak nowego wyglądu nie wyglądał na błąd aktualizacji.
      const keptLayouts = (data.layouts ?? []).filter((l) => l.status !== 'updated');
      if (keptLayouts.length > 0) {
        toast.show({
          variant: 'info',
          duration: 0,
          description: t('tenant.moduleUpdates.layoutsKeptNote', { count: keptLayouts.length }),
        });
      }
      // Odśwież listę aktualizacji + menu/strukturę modułów (nowe pola/encje u klienta).
      void queryClient.invalidateQueries({ queryKey: ['my', 'module-updates'] });
      void queryClient.invalidateQueries({ queryKey: INSTALLED_MODULES_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ['modules'] });
    },
    onError: (err) => {
      // 400 FIELD_SLUG_RESERVED: nowe pole szablonu ma nazwę techniczną zarezerwowaną przez
      // system (np. „decisions") — klient nie zmieni szablonu, więc komunikat kieruje do opiekuna.
      // Tytuł = ogólny błąd aktualizacji, opis = powód; zostaje do ręcznego zamknięcia, bo to
      // treść do przekazania opiekunowi, nie chwilowe powiadomienie (wspólny hak).
      const reserved = showReservedSlugToast(err, {
        title: t('tenant.moduleUpdates.applyError'),
        variant: 'support',
      });
      if (!reserved) {
        toast.show({ variant: 'error', description: t('tenant.moduleUpdates.applyError') });
      }
      // 409 NOTHING_TO_APPLY: wpis był nieaktualny (np. zastosowano już w innej karcie) —
      // refetch usuwa go z modalu zamiast zostawiać wiecznie klikalne „Zastosuj".
      void queryClient.invalidateQueries({ queryKey: ['my', 'module-updates'] });
    },
  });

  const dismissMutation = useMutation({
    mutationFn: async (u: ModuleUpdate) =>
      (
        await api.post(`/my/module-updates/${u.moduleId}/dismiss`, {
          clientVersion: u.latestClientVersion,
        })
      ).data,
    onSuccess: () => {
      // Serwer filtruje pominiętą wersję → refetch usuwa wpis z listy.
      void queryClient.invalidateQueries({ queryKey: ['my', 'module-updates'] });
    },
    onError: () => {
      toast.show({ variant: 'error', description: t('tenant.moduleUpdates.dismissError') });
    },
  });

  const visible = useMemo(() => query.data ?? [], [query.data]);

  if (query.isError || visible.length === 0) return null;

  return (
    <>
      <Banner
        tone="brand"
        icon={ArrowUpCircle}
        data-testid="module-updates-notice"
        actions={
          <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
            {t('tenant.moduleUpdates.view')}
          </Button>
        }
      >
        {t('tenant.moduleUpdates.notice', { count: visible.length })}
      </Banner>

      <Modal open={open} onClose={() => setOpen(false)} title={t('tenant.moduleUpdates.title')}>
        <div className="flex flex-col gap-4" data-testid="module-updates-modal">
          {visible.map((u) => (
            <section
              key={u.moduleId}
              className="rounded-lg border border-gray-200 p-3 dark:border-gray-700"
              data-testid={`module-update-${u.moduleId}`}
            >
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <span className="font-medium text-text-primary">{u.moduleName}</span>
                {u.areaName && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">{u.areaName}</span>
                )}
                <Badge color="blue">
                  {t('tenant.moduleUpdates.versionLine', {
                    installed: u.installedClientVersion,
                    latest: u.latestClientVersion,
                  })}
                </Badge>
                {u.hasBreaking && (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600 dark:text-red-400">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    {t('moduleTemplateEditor.versions.breaking')}
                  </span>
                )}
              </div>

              {u.releaseNotes && (
                <p className="mb-2 text-sm text-gray-700 dark:text-gray-300">{u.releaseNotes}</p>
              )}

              {u.changes.length > 0 ? (
                <ul className="flex flex-col gap-1">
                  {u.changes.map((c, i) => (
                    <li
                      key={`${c.entitySlug}-${c.fieldSlug ?? ''}-${i}`}
                      className="flex items-center gap-2 text-sm"
                    >
                      <Badge color={c.breaking ? 'red' : 'green'}>
                        {c.breaking
                          ? t('moduleTemplateEditor.versions.breakingShort')
                          : t('moduleTemplateEditor.versions.safe')}
                      </Badge>
                      <span className="text-gray-700 dark:text-gray-300">{changeLabel(c, t)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('tenant.moduleUpdates.noChanges')}
                </p>
              )}

              {u.hasBreaking && (
                <p className="mt-2 text-xs text-red-600 dark:text-red-400">
                  {t('tenant.moduleUpdates.breakingHint')}
                </p>
              )}

              <div className="mt-3 flex justify-end gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  loading={
                    dismissMutation.isPending && dismissMutation.variables?.moduleId === u.moduleId
                  }
                  disabled={dismissMutation.isPending}
                  onClick={() => dismissMutation.mutate(u)}
                  data-testid={`module-update-dismiss-${u.moduleId}`}
                >
                  {t('tenant.moduleUpdates.dismiss')}
                </Button>
                {!u.hasBreaking && (
                  <Button
                    variant="primary"
                    size="sm"
                    loading={applyMutation.isPending && applyMutation.variables === u.moduleId}
                    disabled={applyMutation.isPending}
                    onClick={() => applyMutation.mutate(u.moduleId)}
                    data-testid={`module-update-apply-${u.moduleId}`}
                  >
                    {t('tenant.moduleUpdates.apply')}
                  </Button>
                )}
              </div>
            </section>
          ))}
        </div>
      </Modal>
    </>
  );
}
